import { useUserStore } from '~/stores/user';
import { useWebsiteConfigurationStore } from '~/stores/websiteConfigurationStore';
import { useReferralStore } from '~/stores/referralStore';

/**
 * Centralised Google Analytics (GA4) tracking.
 *
 * Wraps nuxt-gtag's `useGtag()` and auto-attaches a set of global params to
 * every event so we don't repeat them in each component:
 *  - logged_in / user_id / verified  -> from the user store
 *  - website_id / tenant / locale / currency -> from the active tenant config
 *  - referral_partner_tracking_code  -> from the referral store (partner iframes)
 *
 * Usage:
 *   const { track } = useAnalytics();
 *   track('booking', { listing_id, listing_title });
 *
 * All calls are client-only and defensively guarded so a tracking failure can
 * never break a user flow.
 */
export function useAnalytics() {
  const { gtag } = useGtag();

  // Collect the params that should ride along on every event.
  const globalParams = () => {
    const params = {};

    try {
      const userStore = useUserStore();
      const loggedIn = !!userStore.user;
      params.logged_in = loggedIn;
      if (loggedIn) {
        if (userStore.user.userId) params.user_id = userStore.user.userId;
        if (userStore.user.verification !== undefined) {
          params.verified = !!userStore.user.verification;
        }
      }
    } catch (e) {
      // store not ready (e.g. SSR) - skip user params
    }

    try {
      const config = useWebsiteConfigurationStore().configuration;
      if (config) {
        if (config.websiteId) params.website_id = config.websiteId;
        if (config.name) params.tenant = config.name;
        if (config.languageCode) params.locale = config.languageCode;
        if (config.currencyCode) params.currency = config.currencyCode.toUpperCase();
      }
    } catch (e) {
      // config store not ready - skip tenant params
    }

    try {
      const partnerCode = useReferralStore().getPartnerCode();
      if (partnerCode) params.referral_partner_tracking_code = partnerCode;
    } catch (e) {
      // referral store not ready - skip partner code
    }

    return params;
  };

  /**
   * Fire a GA4 event. Global params are merged first so an explicit param on a
   * specific event can still override them when intentionally needed.
   */
  const track = (name, params = {}) => {
    if (!process.client) return;
    try {
      gtag('event', name, { ...globalParams(), ...params });
    } catch (e) {
      console.error('[analytics] failed to track event', name, e);
    }
  };

  /**
   * Register GA4 user properties for segmentation. Call after auth state
   * changes so reports can slice by login state / tenant without bloating
   * every event payload.
   */
  const setUserProperties = () => {
    if (!process.client) return;
    try {
      const g = globalParams();
      gtag('set', 'user_properties', {
        logged_in: g.logged_in ?? false,
        verified: g.verified ?? false,
        website_id: g.website_id ?? null,
        tenant: g.tenant ?? null,
      });
    } catch (e) {
      console.error('[analytics] failed to set user properties', e);
    }
  };

  return { track, setUserProperties };
}
